import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Select,
  Flex,
  Heading,
  Checkbox,
  CheckboxGroup,
  Stack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Button,
  Menu,
  MenuButton,
  MenuList,
  RadioGroup,
  Radio,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ReferenceLine,
} from "recharts";
import TotalTabs from "./totalTab";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import axios from "axios";
import { Skeleton } from "@chakra-ui/react";
import { ApplicationContext } from "../utils/contextsProviders";
import { RiArrowDropDownLine } from "react-icons/ri";
import { GrBarChart } from "react-icons/gr";
import { PiChartLineUpBold } from "react-icons/pi";
import { FiInfo } from "react-icons/fi";
import { FaTemperatureHigh } from "react-icons/fa";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import ThinqueLegend from "./ThinqueLegend.tsx";
import { useCountries } from 'use-react-countries';
import AssetRegisterTab from "./AssetRegisterTab";
import PIAMVTab from "./PIAMVTab";
import { IoDocumentTextOutline } from "react-icons/io5";

const normalizeEnergyData = (data) => {
  return data.map((item) => ({
    ...item,
    date: moment(item.timestamp).format("MM/DD/YYYY HH:mm"),
    netZeroStatus: Math.max(
      0,
      Math.round(
        ((item.consumption * 0.65 - (item.gridImport - item.gridExport) * 0.65) /
          1000 /
          ((item.consumption * 0.65 -
            (item.gridImport - item.gridExport) * 0.65) /
            1000 +
            ((item.gridImport - item.gridExport) * 0.65) / 1000)) *
          100
      )
    ),    
    consumption: Math.round(item.consumption / 1000),
    solarElectrictyConsumed: Math.round(
      (item.consumption - item.gridImport) / 1000
    ),
    solarExported: Math.round(item.gridExport / 1000),
    ghgEmissions: Math.round(
      ((item.gridImport - item.gridExport) * 0.69) / 1000
    ),
    ghgAvoided: Math.round(
      (item.consumption * 0.69 - (item.gridImport - item.gridExport) * 0.69) /
        1000
    ),
    active: item.active.reduce((sum, num) => {
      return sum + (Math.round(num / 1000) || 0);
    }, 0),
    reactive: item.reactive.reduce((sum, num) => {
      return sum + (Math.round(num / 1000) || 0);
    }, 0),
    powerFactor:
      parseFloat(
        (
          1 +
          item.reactive.reduce((sum, num) => {
            return sum + (Math.round(num / 1000) || 0);
          }, 0) /
            (item.active.reduce((sum, num) => {
              return sum + (Math.round(num / 1000) || 0);
            }, 0) -
              item.reactive.reduce((sum, num) => {
                return sum + (Math.round(num / 1000) || 0);
              }, 0))
        ).toFixed(2)
      ) * 100,
  }));
};

const checkboxStyle = {
  ".chakra-checkbox__control": {
    bg: "transparent",
    borderColor: "black",
    color: "black",
  },
  ".chakra-checkbox__control[data-checked]": {
    bg: "transparent",
    borderColor: "black",
  },
  ".chakra-checkbox__control[data-checked] svg": {
    color: "black",
  },
  ".chakra-checkbox__control[data-hover]": {
    borderColor: "black",
  },
  ".chakra-checkbox__label": {
    fontSize: "15px",
  },
};

function formatDate(inputDate) {
  const date = new Date(inputDate);
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
  const minutes = String(Math.abs(offset) % 60).padStart(2, "0");
  const isoString = date.toISOString();
  const formattedDate = isoString.replace(".000Z", "");
  return `${formattedDate}${sign}${hours}:${minutes}`;
}

const Dashboard = () => {
  const [refreshToken, setRefreshToken] = useState("");
  const [locations, setLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [location, setLocation] = useState("");
  const [currentLocation, setCurrentLocation] = useState("");
  const [carbonEmission, setCarbonEmission] = useState("");
  const [environmental, setEnvironmental] = useState("");
  const [timePeriod, setTimePeriod] = useState("week");
  const [energyStartDate, setEnergyStartDate] = useState(new Date());
  const [gasStartDate, setGasStartDate] = useState(new Date());
  const [waterStartDate, setWaterStartDate] = useState(new Date());
  const [weatherStartDate, setWeatherStartDate] = useState(new Date());
  const [energyEndDate, setEnergyEndDate] = useState(new Date());
  const [gasEndDate, setGasEndDate] = useState(new Date());
  const [waterEndDate, setWaterEndDate] = useState(new Date());
  const [weatherEndDate, setWeatherEndDate] = useState(new Date());
  const [energyData, setEnergyData] = useState([]);
  const [energyChartData, setEnergyChartData] = useState([]);
  const [gasData, setGasData] = useState([]);
  const [gasChartData, setGasChartData] = useState([]);
  const [waterChartData, setWaterChartData] = useState([]);
  const [weatherData, setWeatherData] = useState([]);
  const [weatherChartData, setWeatherChartData] = useState([]);
  const [selectedUtilities, setSelectedUtilities] = useState(["consumption"]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedOthers, setSelectedOthers] = useState([""]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedLocationType, setSelectedLocationType] = useState("commercial");
  const [filteredLocations, setFilteredLocations] = useState([]);
  const { user } = useContext(ApplicationContext);
  const [selectedCountries, setSelectedCountries] = useState(["Australia"]);

  const { countries } = useCountries();
  const sortedCountries = [...countries].sort((a, b) => a.name.localeCompare(b.name));

  const allStates = {
    "Australia": [
      "NSW - New South Wales",
      "QLD - Queensland",
      "VIC - Victoria",
      "SA - South Australia",
      "TAS - Tasmania",
      "WA - Western Australia",
      "NT - Northern Territory"
    ],
    "Belgium": [
      "FR - Flemish Region",
      "WR - Walloon Region",
      "BCR - Brussels-Capital Region"
    ]
  };

  const locationTypes = [
    { value: "commercial", label: "Commercial" },
    { value: "residential", label: "Residential" }
  ];

  const handleStatesChange = (selected) => {
    setSelectedStates(selected);
  };

  const tenantId = "t3045089";
  const username = `${tenantId}/info@netzeroce.com.au`;
  const password = "NetZero@2015";
  const authHeader = `Basic ${btoa(username + ":" + password)}`;
  const newStartDate = new Date(
    energyStartDate.getTime() + 24 * 60 * 60 * 1000
  ).toString();

  const queryParams = {
    dateFrom: formatDate(newStartDate),
    dateTo: formatDate(energyEndDate),
    revert: "true",
    series: "Pulse1_Total.T1",
    source: "8136182759",
    pageSize: "2000",
  };

  const queryString = new URLSearchParams(queryParams).toString();

  const getUniqueEntriesByTime = (entries) => {
    const uniqueEntries = new Map();
    entries.forEach((entry) => {
      uniqueEntries.set(entry.time, entry);
    });
    return Array.from(uniqueEntries.values());
  };

  useEffect(() => {
    const fetchReadings = async () => {
      try {
        const response = await fetch(
          `https://proxy.cors.sh/https://t3045089.apj.cumulocity.com/measurement/measurements?${queryString}`,
          {
            headers: {
              Authorization: authHeader,
            },
          }
        );
        const data = await response.json();
        const Pulse1_Totals = data.measurements.filter(
          (entry) => entry.type === "Pulse1_Total"
        );
        const uniqueItems = getUniqueEntriesByTime(Pulse1_Totals);
        const transformedArray = uniqueItems.map((item) => ({
          date: item.time,
          KL: item.Pulse1_Total.T1.value,
        }));
        setWaterChartData(transformedArray);
      } catch (error) {
        console.error("Error fetching water readings:", error);
      }
    };
    fetchReadings();
  }, [energyStartDate]);

  useEffect(() => {
    setLocationLoading(true);
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/smappee/service-locations`)
        .then((response) => {
          const locationData = response.data.serviceLocations.map(
            (location) => ({
              serviceLocationId: location.serviceLocationId,
              name: location.name,
            })
          );
          setAllLocations(locationData);
          setLocations(
            (user.role === "SuperAdmin" ? locationData : user.locations)
              .filter((location) => {
                const locationName = user.role === "SuperAdmin" ? location.name : location;
                return (
                  !locationName.includes("CABIN") &&
                  !locationName.includes("Elvy") &&
                  !locationName.includes("Academy")
                );
              })
              .map((location) => ({
                ...(user.role === "SuperAdmin" ? { ...location } : { name: location }),
                state: (user.role === "SuperAdmin" ? location.name : location).includes("Smappee Academy")?"BCR - Brussels-Capital Region":"NSW - New South Wales",
              }))
          );
          setLocationLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching locations:", error);
        });
  }, []);

  useEffect(() => {
    if (location) {
      const fetchEnergyData = async () => {
        const today = new Date();
        setEnergyEndDate(today);
        const todayTimestamp = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        ).getTime();
        let fromTimestamp;
        let toTimestamp = todayTimestamp;
        let aggregation = 3;

        switch (timePeriod) {
          case "day":
            const yesterday = new Date(
              today.getFullYear(),
              today.getMonth(),
              today.getDate() - 1
            );
            fromTimestamp = new Date(yesterday.setHours(0, 0, 0, 0)).getTime();
            toTimestamp = new Date(yesterday.setHours(23, 59, 59, 999)).getTime();
            aggregation = 2;
            setEnergyStartDate(new Date(fromTimestamp));
            setEnergyEndDate(new Date(toTimestamp));
            break;
          
          case "week":
            const now = new Date();
            const lastMonday = new Date(now);
            lastMonday.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
            lastMonday.setHours(0, 0, 0, 0);
            const lastSunday = new Date(lastMonday);
            lastSunday.setDate(lastMonday.getDate() + 6);
            lastSunday.setHours(23, 59, 59, 999);
            fromTimestamp = lastMonday.getTime();
            toTimestamp = lastSunday.getTime();
            setEnergyStartDate(new Date(fromTimestamp));
            setEnergyEndDate(new Date(toTimestamp));
            break;


          case "month":
            const lastMonth = new Date(
              today.getFullYear(),
              today.getMonth() - 1,
              1
            );
            fromTimestamp = lastMonth.getTime();
            toTimestamp = new Date(
              today.getFullYear(),
              today.getMonth(),
              0,
              23,
              59,
              59,
              999
            ).getTime();
            aggregation = 3;
            setEnergyStartDate(new Date(fromTimestamp));
            setEnergyEndDate(new Date(toTimestamp));
            break;

          case "quarter":
            const currentQuarter = Math.floor((today.getMonth() + 3) / 3);
            const lastQuarterStartMonth = currentQuarter * 3 - 6;
            const lastQuarterStart = new Date(
              today.getFullYear(),
              lastQuarterStartMonth,
              1
            );
            fromTimestamp = lastQuarterStart.getTime();
            toTimestamp = new Date(
              today.getFullYear(),
              lastQuarterStartMonth + 3,
              0,
              23,
              59,
              59,
              999
            ).getTime();
            aggregation = 3;
            setEnergyStartDate(new Date(fromTimestamp));
            setEnergyEndDate(new Date(toTimestamp));
            break;

          case "half-year":
            const currentHalfYear = today.getMonth() < 6 ? 1 : 2;
            const lastHalfYearStart = new Date(
              today.getFullYear(),
              currentHalfYear === 1 ? 0 : 6,
              1
            );
            fromTimestamp = lastHalfYearStart.getTime();
            toTimestamp = new Date(
              today.getFullYear(),
              currentHalfYear === 1 ? 6 : 12,
              0,
              23,
              59,
              59,
              999
            ).getTime();
            aggregation = 4;
            setEnergyStartDate(new Date(fromTimestamp));
            setEnergyEndDate(new Date(toTimestamp));
            break;

          case "year":
            const lastFiscalYearStart = new Date(
              today.getMonth() >= 6
                ? today.getFullYear() - 1
                : today.getFullYear() - 2,
              6,
              1
            );
            fromTimestamp = lastFiscalYearStart.getTime();
            toTimestamp = new Date(
              lastFiscalYearStart.getFullYear() + 1,
              5,
              30,
              23,
              59,
              59,
              999
            ).getTime();
            aggregation = 4;
            setEnergyStartDate(new Date(fromTimestamp));
            setEnergyEndDate(new Date(toTimestamp));
            break;

          case "2years":
            const twoYearsStart = new Date(
              today.getMonth() >= 6
                ? today.getFullYear() - 2
                : today.getFullYear() - 3,
              6,
              1
            );
            fromTimestamp = twoYearsStart.getTime();
            toTimestamp = new Date(
              twoYearsStart.getFullYear() + 2,
              5,
              30,
              23,
              59,
              59,
              999
            ).getTime();
            aggregation = 4;
            setEnergyStartDate(new Date(fromTimestamp));
            setEnergyEndDate(new Date(toTimestamp));
            break;
          default:
            fromTimestamp = todayTimestamp;
        }

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/smappee/consumption/${location}`,
            {
              params: { aggregation, from: fromTimestamp, to: toTimestamp },
            }
          );
          setEnergyData(normalizeEnergyData(response.data.consumptions));
          setEnergyChartData(normalizeEnergyData(response.data.consumptions));
          setDataLoading(false);
        } catch (error) {
          console.error("Error fetching energy data:", error);
          setDataLoading(false);
        }
      };
      fetchEnergyData();
    }
  }, [location, timePeriod]);

  useEffect(() => {
    setLocation('');
  }, [selectedStates]);

  useEffect(() => {
    if (locations && locations.length > 0) {
      const isBelgiumSelected = selectedCountries.includes("Belgium");
      const isAustraliaSelected = selectedCountries.includes("Australia");
      
      const isBCRSelected = selectedStates.includes("BCR - Brussels-Capital Region");
      const isNSWSelected = selectedStates.includes("NSW - New South Wales");
  
      let filtered = [];
  
      if (isBelgiumSelected && selectedStates.length === 1 && isBCRSelected) {
        filtered = locations.filter(
          (location) => location.state === "BCR - Brussels-Capital Region"
        );
      } else if (isAustraliaSelected && selectedStates.length === 1 && isNSWSelected) {
        filtered = locations.filter(
          (location) => location.state === "NSW - New South Wales"
        );
      } else if (isBelgiumSelected && isAustraliaSelected && selectedStates.includes("BCR - Brussels-Capital Region") && selectedStates.includes("NSW - New South Wales")) {
        filtered = locations.filter(
          (location) => location.state === "BCR - Brussels-Capital Region" || location.state === "NSW - New South Wales"
        );
      }
      else if(selectedCountries.length === 1 && selectedStates.length > 0 && !(isBCRSelected || isNSWSelected)){
        filtered = [];
      } else {
        filtered = locations.filter(
          (location) => selectedStates.includes(location.state)
        );
      }

      // Further filter by location type
      filtered = filtered.filter(
        (location) =>
          selectedLocationType === "residential"
            ? !location.name.includes("-P-") && !location.name.includes("-A-")
            : location.name.includes("-P-") || location.name.includes("-A-")
      );

      if (selectedLocationType === "commercial") {
        filtered.sort((a, b) => {
          const numA = parseInt(a.name.split('-')[0], 10);
          const numB = parseInt(b.name.split('-')[0], 10);
          if (numA !== numB) {
            return numA - numB;
          }
          return a.name.localeCompare(b.name);
        });
      }
      
      setFilteredLocations(filtered);
    }
    setLocation('');
  }, [locations, selectedCountries, selectedStates, selectedLocationType]);

  const yAxisDomain = [
    Math.min(...energyChartData.map((item) => item.value || 0)),
    Math.max(...energyChartData.map((item) => item.value || 0)),
  ];

  const yAxisDomainWater = [
    Math.min(...waterChartData.map((item) => item.date || 0)),
    Math.max(...waterChartData.map((item) => item.date || 0)),
  ];

  const downloadChartsAsPDF = () => {
    const charts = document.querySelectorAll(".recharts-responsive-container");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth(); // PDF width
    const chartHeight = 40; // Set the height for each chart
    const margin = 8; // Margin between charts

    let promises = [];
    charts.forEach((chart, index) => {
      promises.push(
        html2canvas(chart).then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          const imgProps = pdf.getImageProperties(imgData);
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width; // Maintain aspect ratio

          const positionY = margin + (index % 6) * (chartHeight + margin); // Position on Y-axis

          // Add new page every 3 charts
          if (index > 0 && index % 6 === 0) {
            pdf.addPage();
          }

          // Center the chart and scale to fit
          const centeredX = (pdfWidth - pdfWidth) / 2;

          pdf.addImage(
            imgData,
            "PNG",
            centeredX,
            positionY,
            pdfWidth,
            chartHeight
          ); // Adjust width and height to fit
        })
      );
    });

    Promise.all(promises).then(() => {
      pdf.save("charts.pdf");
    });
  };

  const handleCountryChange = (selectedCountriesArray) => {
    setSelectedCountries(selectedCountriesArray);
    // Reset states and locations when countries change
    setSelectedStates([]);
    setLocation('');
    setCurrentLocation('');
  };

  const handleLocationTypeChange = (value) => {
    setSelectedLocationType(value);
  };

  const getServiceLocationId = (locationName) => {
    const serviceLocation = allLocations.find(serviceLocation => serviceLocation.name === locationName);
    return serviceLocation ? serviceLocation.serviceLocationId : null; 
  };

  return (
    <Box px={{ base: 0, md: 2 }}>
      <Tabs>
        <TabList>
          <Tab
            fontWeight={"bold"}
            gap={2}
            _selected={{
              color: "white",
              backgroundColor: "black",
            }}
          >
            Totals
            <GrBarChart size={15} />
          </Tab>
          <Tab
            fontWeight={"bold"}
            gap={2}
            _selected={{
              color: "white",
              backgroundColor: "black",
            }}
          >
            Trends
            <PiChartLineUpBold size={17} />
          </Tab>
          <Tab
            fontWeight={"bold"}
            gap={2}
            _selected={{
              color: "white",
              backgroundColor: "black",
            }}
          >
            Asset Register / Reports
            <IoDocumentTextOutline size={17}/>
          </Tab>
          <Tab
            fontWeight={"bold"}
            gap={2}
            _selected={{
              color: "white",
              backgroundColor: "black",
            }}
          >
            PIAM&V
            <FaTemperatureHigh size={17} />
          </Tab>
          <Tab
            fontWeight={"bold"}
            gap={2}
            _selected={{
              color: "white",
              backgroundColor: "black",
            }}
          >
            Legend
            <FiInfo size={17} />
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>
            <TotalTabs
              locations={user.role==="SuperAdmin" ? locations : locations.map(location => {
                const serviceLocationId = getServiceLocationId(location.name);
                return {
                  name: location.name,
                  state: location.state,
                  serviceLocationId: serviceLocationId
                };
              })}
              locationLoading={locationLoading}
            />
          </TabPanel>
          <TabPanel padding={2}>
            <Flex
              justifyContent={"space-between"}
              direction={{ base: "column", md: "row" }}
              gap={{ base: 5, md: "auto" }}
            >
              <Flex
                direction={"column"}
                gap={3}
                width={{ base: "100%", md: "29%" }}
                // border={"1px solid grey"}
                padding={{ base: 3, md: 5 }}
                height={"82vh"}
                overflowY={"auto"}
                backgroundColor={"white"}
                boxShadow={"0 4px 8px rgba(85,37,9,.05)"}
              >
                {/* <Heading size={"md"}>{`Customer Account: NZCE`}</Heading> */}
                {/* <Heading size={"md"}>{`Customer Account: ${user.company_name}`}</Heading> */}
                <Menu closeOnSelect={false}>
                  <MenuButton
                    fontWeight={500}
                    as={Button}
                    width="100%"
                    textAlign="left"
                    rightIcon={<RiArrowDropDownLine />}
                    fontSize={{ base: "sm", md: "md" }}
                    padding={{ base: 2, md: 3 }}
                  >
                    Select Countries
                  </MenuButton>
                  <MenuList
                    display="flex"
                    width="100%"
                    minWidth={{ base: "90vw", md: "250px" }}
                    maxHeight="300px"
                    overflowY="auto"
                    padding="9px"
                    flexDirection="column"
                  >
                    <CheckboxGroup
                      colorScheme="black"
                      onChange={handleCountryChange}
                      value={selectedCountries}
                    >
                      <Stack direction="column" spacing={2}>
                        {sortedCountries.map(({name}) => (
                          <Checkbox key={name} value={name} sx={checkboxStyle} fontSize={{ base: "sm", md: "md" }} fontWeight={name==="Australia" || name ==="Belgium"?"700":""}>
                            {name}
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                  </MenuList>
                </Menu>

                {selectedCountries.length > 0 ? 
                <Menu>
                <MenuButton
                  fontWeight={500}
                  as={Button}
                  width={"100%"}
                  padding={"10px 8px 10px 16px"}
                  textAlign={"left"}
                  rightIcon={<RiArrowDropDownLine size={20}/>}
                >
                  Select States
                </MenuButton>
                <MenuList
                  display={"flex"}
                  width={"content-fit"}
                  padding={"9px"}
                  flexDirection={"column"}
                >
                  {<CheckboxGroup
                    colorScheme="black"
                    onChange={handleStatesChange}
                    value={selectedStates}
                  >
                    {selectedCountries.includes('Australia') && (
                      <>
                        <Text fontWeight="bold" px={2} mt={1}>Australia</Text>
                        {allStates['Australia'].map((state) => (
                          <Checkbox key={`AU-${state}`} value={state} sx={checkboxStyle}>
                            {state}
                          </Checkbox>
                        ))}
                      </>
                    )}
                    {selectedCountries.includes('Belgium') && (
                      <>
                        <Text fontWeight="bold" px={2} mt={2}>Belgium</Text>
                        {allStates['Belgium'].map((state) => (
                          <Checkbox key={`BE-${state}`} value={state} sx={checkboxStyle}>
                            {state}
                          </Checkbox>
                        ))}
                      </>
                    )}
                  </CheckboxGroup>
                  }
                </MenuList>
                </Menu>:
                <p>No countries selected</p>}
                {selectedStates.length > 0 ?
                <Menu>
                  <MenuButton
                    fontWeight={500}
                    as={Button}
                    width="100%"
                    textAlign="left"
                    rightIcon={<RiArrowDropDownLine />}
                    fontSize={{ base: "sm", md: "md" }}
                    padding={{ base: 2, md: 3 }}
                  >
                    Select Location Type
                  </MenuButton>
                  <MenuList
                    display="flex"
                    width="100%"
                    minWidth={{ base: "90vw", md: "250px" }}
                    maxHeight="300px"
                    overflowY="auto"
                    padding="9px"
                    flexDirection="column"
                  >
                    <RadioGroup 
                      value={selectedLocationType} 
                      onChange={handleLocationTypeChange}
                    >
                      <Stack direction="column">
                        {locationTypes.map((type) => (
                          <Radio key={type.value} value={type.value} sx={checkboxStyle} fontSize={{ base: "sm", md: "md" }}>
                            {type.label}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                  </MenuList>
                </Menu>
                :
                selectedStates.length > 0 ? <p>No locations found for this state</p> : <Skeleton height="40px" width="100%" />}
                {locationLoading ? (
                  <Stack>
                    <Skeleton height="30px" width="100px" marginBottom={2} />
                    <Skeleton height="40px" marginBottom={3} />
                    <Skeleton height="30px" width="100px" marginBottom={2} />
                    <Skeleton height="25px" width="100px" marginBottom={2} />
                    <Skeleton height="20px" width="230px" />
                    <Skeleton height="20px" width="160px" />
                    <Skeleton height="20px" width="175px" />
                    <Skeleton height="20px" width="195px" />
                    <Skeleton height="20px" width="110px" marginBottom={2} />
                    <Skeleton height="20px" width="90px" marginBottom={2} />
                    <Skeleton height="20px" width="130px" />
                  </Stack>
                ) : (
                  <Flex direction={"column"} gap={5}>
                    {/* <Heading size={"sm"}>Location(s)</Heading> */}
                    {selectedStates.length > 0 && filteredLocations.length?
                    <Select
                      placeholder="Select Location"
                      value={location}
                      variant="filled"
                      onChange={(e) => {
                        const selectedLocationId = user.role === "SuperAdmin" ? e.target.value : (allLocations.find(
                          (loc) => loc.name === e.target.value
                        )).serviceLocationId;
                        const selectedLocation = (user.role === "SuperAdmin" ? locations : allLocations).find(
                          (loc) => loc.serviceLocationId.toString() === selectedLocationId
                        );
                        setLocation(selectedLocationId);
                        setCurrentLocation(selectedLocation?.name || "");
                      }}
                      icon={<RiArrowDropDownLine />}
                    >
                      {filteredLocations.map((loc, index) => (
                        <option key={index} value={loc.serviceLocationId}>
                          {loc.name}
                        </option>
                      ))}
                    </Select>:
                    selectedStates.length>0?<p>No locations found for this state</p>:<Skeleton height="40px" width="100%" />}
                    {location && <Select
                      placeholder="Select Time Period"
                      value={timePeriod}
                      variant="filled"
                      onChange={(e) => setTimePeriod(e.target.value)}
                      icon={<RiArrowDropDownLine />}
                    >
                      <option value={"day"}>Day</option>
                      <option value={"week"}>Week</option>
                      <option value={"month"}>Month</option>
                      <option value={"quarter"}>Quarter</option>
                      <option value={"half-year"}>Half Year</option>
                      <option value={"year"}>Year</option>
                      <option value={"2years"}>2 Years</option>
                    </Select>}
                    {dataLoading || !location ? (
                      <Stack>
                        <Skeleton
                          height="30px"
                          width="100px"
                          marginBottom={3}
                        />
                        <Skeleton
                          height="25px"
                          width="100px"
                          marginBottom={3}
                        />
                        <Skeleton height="20px" width="230px" />
                        <Skeleton height="20px" width="160px" />
                        <Skeleton height="20px" width="175px" />
                        <Skeleton height="20px" width="195px" />
                        <Skeleton
                          height="20px"
                          width="110px"
                          marginBottom={3}
                        />
                        <Skeleton height="20px" width="90px" marginBottom={3} />
                        <Skeleton height="20px" width="130px" />
                      </Stack>
                    ) : (
                      <>
                        <fieldset
                          style={{
                            border: "2px solid black",
                            padding: "10px",
                            borderRadius: "5px",
                          }}
                        >
                          <legend
                            style={{
                              color: "black",
                              fontSize: "1rem",
                              padding: "0 5px",
                            }}
                          >
                            Power :
                          </legend>
                          <CheckboxGroup
                            colorScheme="black"
                            value={selectedUtilities}
                            onChange={setSelectedUtilities}
                          >
                            <Stack direction="column">
                              <Stack direction="row" spacing={4}>
                                {
                                  <Checkbox
                                    sx={checkboxStyle}
                                    width={"50%"}
                                    value="net-zero-status"
                                  >
                                    BNZE Status
                                  </Checkbox>
                                }
                                <Checkbox
                                  value="consumption"
                                  sx={checkboxStyle}
                                  width={"50%"}
                                >
                                  Total Electricity Consumption
                                </Checkbox>
                              </Stack>
                              {currentLocation !== "SMITHS LAKE  BUTCHERS" &&
                              <Stack direction="row" spacing={4}>
                                <Checkbox
                                  value="solar-consumed"
                                  sx={checkboxStyle}
                                  width={"50%"}
                                >
                                  Solar Electricity Consumed
                                </Checkbox>
                                <Checkbox
                                  value="solar-exported"
                                  sx={checkboxStyle}
                                  width={"50%"}
                                >
                                  Solar Exported to Grid
                                </Checkbox>
                              </Stack>}
                              {currentLocation !== "NRMA PORT MACQUARIE SWITCHBOARD EAST" &&
                              <Stack direction="row" spacing={4}>
                                <Checkbox
                                  value="ghg-emissions"
                                  sx={checkboxStyle}
                                  width={"50%"}
                                >
                                  GHG Emissions (Net)
                                </Checkbox>
                                <Checkbox
                                  value="ghg-avoided"
                                  sx={checkboxStyle}
                                  width={"50%"}
                                >
                                  GHG Avoided
                                </Checkbox>
                              </Stack>}
                              <Stack direction="row" spacing={4}>
                                <Checkbox
                                  value="activePower"
                                  sx={checkboxStyle}
                                  width={"50%"}
                                >
                                  Demand Active (kVAh)
                                </Checkbox>
                                <Checkbox
                                  value="reactivePower"
                                  sx={checkboxStyle}
                                  width={"50%"}
                                >
                                  Demand Reactive (kVARh)
                                </Checkbox>
                              </Stack>
                              <Stack direction="row" spacing={4}>
                                <Checkbox
                                  sx={checkboxStyle}
                                  width={"50%"}
                                  value="power-factor"
                                >
                                  Power Factor
                                </Checkbox>
                              </Stack>
                            </Stack>
                          </CheckboxGroup>
                        </fieldset>

                        {currentLocation.toLowerCase() === ("0002-P-Smiths Lake NSW - Nadine The Cake Queen").toLowerCase() && 
                        <fieldset
                          style={{
                            border: "2px solid black",
                            padding: "10px",
                            borderRadius: "5px",
                          }}
                        >
                          <legend
                            style={{
                              color: "black",
                              fontSize: "1rem",
                              padding: "0 5px",
                            }}
                          >
                            Utilities :
                          </legend>
                          <CheckboxGroup
                            colorScheme="black"
                            value={selectedOthers}
                            onChange={setSelectedOthers}
                          >
                            <Checkbox
                              value="water"
                              sx={checkboxStyle}
                              width={"50%"}
                            >
                              Water
                            </Checkbox>
                          </CheckboxGroup>
                        </fieldset>}

                        {currentLocation.toLowerCase() === ("0002-P-Smiths Lake NSW - Nadine The Cake Queen").toLowerCase() && <fieldset
                          style={{
                            border: "2px solid black",
                            padding: "10px",
                            borderRadius: "5px",
                          }}
                        >
                          <legend
                            style={{
                              color: "black",
                              fontSize: "1rem",
                              padding: "0 5px",
                            }}
                          >
                            Environmental :
                          </legend>
                          <CheckboxGroup
                            colorScheme="black"
                            value={environmental}
                            onChange={setEnvironmental}
                          >
                            <Stack direction="column">
                              <Stack direction="row" spacing={4}>
                                <Checkbox
                                  sx={checkboxStyle}
                                  width={"50%"}
                                  value="co2"
                                >
                                  CO2
                                </Checkbox>
                                <Checkbox
                                  sx={checkboxStyle}
                                  width={"50%"}
                                  value="pm2.5"
                                >
                                  PM2.5
                                </Checkbox>
                              </Stack>
                              <Stack direction="row" spacing={4}>
                                <Checkbox
                                  sx={checkboxStyle}
                                  width={"50%"}
                                  value="tvoc"
                                >
                                  TVOC
                                </Checkbox>
                                <Checkbox
                                  sx={checkboxStyle}
                                  width={"50%"}
                                  value="temperature"
                                >
                                  Indoor Temperature
                                </Checkbox>
                              </Stack>
                              <Stack direction="row" spacing={4}>
                                <Checkbox
                                  sx={checkboxStyle}
                                  width={"50%"}
                                  value="humidity"
                                >
                                  Indoor Humidity
                                </Checkbox>
                              </Stack>
                            </Stack>
                          </CheckboxGroup>
                        </fieldset>}
                      </>
                    )}
                  </Flex>
                )}
              </Flex>

              <Box
                width={{ base: "103%", md: "70%" }}
                height={"82vh"}
                overflowY={"auto"}
              >
                {dataLoading || locationLoading || !location ? (
                  <Stack padding={"10px"}>
                    <Skeleton height="25vh" width={"100%"} marginBottom={3} />
                    <Skeleton height="25vh" width={"100%"} marginBottom={3} />
                    <Skeleton height="25vh" width={"100%"} />
                  </Stack>
                ) : (
                  <Box
                    display={"flex"}
                    flexDir={"column"}
                    gap={5}
                    alignItems={"start"}
                  >
                    <Flex
                      direction={"row"}
                      gap={"10px"}
                      alignItems={"baseline"}
                    >
                      <Heading m={0} size={"sm"} color={"black"}>
                        Time Period:{" "}
                      </Heading>
                      <Text size={"md"}>
                        {energyStartDate.toLocaleDateString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                        }) ===
                        energyEndDate.toLocaleDateString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                        })
                          ? energyEndDate.toLocaleDateString("en-US", {
                              month: "short",
                              day: "2-digit",
                              year: "numeric",
                            })
                          : `${energyStartDate.toLocaleDateString("en-US", {
                              month: "short",
                              day: "2-digit",
                              year: "numeric",
                            })} - ${energyEndDate.toLocaleDateString("en-US", {
                              month: "short",
                              day: "2-digit",
                              year: "numeric",
                            })}`}
                      </Text>
                    </Flex>
                    {selectedUtilities.includes("net-zero-status") && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={energyChartData}
                          style={{ background: "transparent" }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" interval={10} />
                          <YAxis domain={yAxisDomain} />
                          <Tooltip />
                          <Legend />
                          <ReferenceLine y={100} stroke="red" strokeDasharray="20 2" />
                          <Area
                            type="monotone"
                            name="BNZE Status (%)"
                            dataKey="netZeroStatus"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {selectedUtilities.includes("consumption") && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={energyChartData}
                          style={{ background: "transparent" }}
                          grid="off"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" interval={10} />
                          <YAxis domain={yAxisDomain} />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            name="Total Electricity Consumption (kWh)"
                            dataKey="consumption"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {selectedUtilities.includes("solar-consumed") && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={energyChartData}
                          style={{ background: "transparent" }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" interval={10} />
                          <YAxis domain={yAxisDomain} />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            name="Solar Electricity Consumed (kWh)"
                            dataKey="solarElectrictyConsumed"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {selectedUtilities.includes("solar-exported") && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={energyChartData}
                          style={{ background: "transparent" }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" interval={10} />
                          <YAxis domain={yAxisDomain} />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            name="Solar Exported To Grid (kWh)"
                            dataKey="solarExported"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {selectedUtilities.includes("ghg-emissions") && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={energyChartData}
                          style={{ background: "transparent" }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" interval={10} />
                          <YAxis domain={yAxisDomain} />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            name="GHG Emissions (tonnes)"
                            dataKey="ghgEmissions"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {selectedUtilities.includes("ghg-avoided") && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={energyChartData}
                          style={{ background: "transparent" }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" interval={10} />
                          <YAxis domain={yAxisDomain} />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            name="GHG Avoided (tonnes)"
                            dataKey="ghgAvoided"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {selectedUtilities.includes("activePower") && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={energyChartData}
                          style={{ background: "transparent" }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" interval={10} />
                          <YAxis domain={yAxisDomain} />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            name="Active (kVAh)"
                            dataKey="active"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {selectedUtilities.includes("reactivePower") && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={energyChartData}
                          style={{ background: "transparent" }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" interval={10} />
                          <YAxis domain={yAxisDomain} />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            name="Reactive (kVARh)"
                            dataKey="reactive"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {selectedUtilities.includes("power-factor") && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={energyChartData}
                          style={{ background: "transparent" }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" interval={10} />
                          <YAxis domain={yAxisDomain} />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            name="Power Factor"
                            dataKey="powerFactor"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {selectedOthers.includes("water") && (currentLocation.toLowerCase() === ("0002-P-Smiths Lake NSW - Nadine The Cake Queen").toLowerCase()) && (
                      <ResponsiveContainer width="97%" height={220}>
                        <AreaChart
                          data={waterChartData}
                          style={{
                            background: "transparent",
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis domain={yAxisDomainWater} />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="KL"
                            stroke="black"
                            fill="black"
                            activeDot={{ r: 5 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {/* <ResponsiveContainer width="97%" height={220}>
                      <LineChart
                        data={weatherChartData}
                        style={{
                          background: "white",
                          padding: "7px",
                          boxShadow: "0 4px 8px rgba(85,37,9,.05)",
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={yAxisDomainWeather} />
                        <Tooltip />
                        <Legend />
                        {environmental === "pressure" && (
                          <Line
                            type="monotone"
                            dataKey="pressure"
                            stroke="#ff7300"
                          />
                        )}
                        {environmental === "noise" && (
                          <Line
                            type="monotone"
                            dataKey="noise"
                            stroke="#82ca9d"
                          />
                        )}
                        {environmental === "co2" && (
                          <Line
                            type="monotone"
                            dataKey="co2"
                            stroke="#ff7300"
                          />
                        )}
                        {environmental === "temperature" && (
                          <Line
                            type="monotone"
                            dataKey="temperature"
                            stroke="#ff7300"
                          />
                        )}
                        {environmental === "humidity" && (
                          <Line
                            type="monotone"
                            dataKey="humidity"
                            stroke="#ff7300"
                          />
                        )}
                        {environmental === "outsideTemperature" && (
                          <Line
                            type="monotone"
                            dataKey="outsideTemperature"
                            stroke="#ff7300"
                          />
                        )}
                        {environmental === "outsideHumidity" && (
                          <Line
                            type="monotone"
                            dataKey="outsideHumidity"
                            stroke="#ff7300"
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer> */}

                    <Button
                      className="download_btn"
                      mt={4}
                      width={{ base: "100%", md: "auto" }}
                      backgroundColor="black"
                      color="white"
                      onClick={downloadChartsAsPDF}
                    >
                      Download Report
                    </Button>
                  </Box>
                )}
              </Box>
            </Flex>
          </TabPanel>
          <TabPanel padding={2}>
            <AssetRegisterTab />
          </TabPanel>
          <TabPanel padding={2}>
            <PIAMVTab />
          </TabPanel>
          <TabPanel p={2}>
            <ThinqueLegend/>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default Dashboard;
